import { pipelineCapacityDisplay } from '../../lib/pipeline-capacity';
import { pipelinePotential } from '../../lib/pipeline-reference';
import { pendingFemaLabels, pendingFloodLayers, PENDING_FEMA_COLOR } from '../../lib/fema-pending';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS, toneStyle } from './tokens';
import { getMapboxToken } from '../../lib/config';
import { useMapMeasurement } from '../../hooks/useMapMeasurement';
import type { PortfolioRow, PortfolioPad } from '../../lib/portfolioApi';

type MapStyleMode = 'light' | 'satellite';
const STYLE_URLS: Record<MapStyleMode, string> = {
  light: 'mapbox://styles/mapbox/light-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};

interface Props {
  rows: PortfolioRow[];
  cycleBbox: [number, number, number, number] | null;
  /** Dot click — typically setActiveParcelId in the parent. Does NOT navigate. */
  onSelect?: (parcelId: string) => void;
  /** Dot hover — typically setHoveredParcelId in the parent for row sync. */
  onHover?: (parcelId: string | null) => void;
  height?: number | string;
  stuckHeight?: number;
  sticky?: boolean;
  /** External active selection — drives the accent-ring layer + flyTo. */
  activeParcelId?: string | null;
  /** Portfolio share-link token. When present, sub-fetches (pipelines overlay)
      authenticate via ?token= instead of localStorage Bearer. */
  shareToken?: string;
  /** Crown Pine research mode — exposes the "All CP" owner-parcel layer + a
      slope-shading toggle (fetched from /api/crownpine-parcels). Off for the
      Integra portfolio, which never passes it, so its map is unchanged. */
  ownerParcels?: boolean;
  /** Crown Pine research mode — the per-pad breakdown. When present, the
      clean-pad layer draws one INDEPENDENT feature per pad (each clickable /
      hoverable), and pad selection drives its own highlight + flyTo. Integra
      never passes it, so its clean-pad layer stays the per-block MultiPolygon. */
  pads?: PortfolioPad[];
  /** Externally-driven active PAD — highlights that pad (no zoom; see focusPadId). */
  activePadId?: string | null;
  /** Pad to ZOOM to — set only when a pad's side panel is opened, so mere
      selection just highlights and the map doesn't lurch while scanning. */
  focusPadId?: string | null;
  /** Row-hover sync for pads (highlights the hovered pad's outline). */
  hoveredPadId?: string | null;
  /** Pad click — typically setActivePadId in the parent. */
  onSelectPad?: (padId: string) => void;
  /** Pad hover — typically setHoveredPadId in the parent for row sync. */
  onHoverPad?: (padId: string | null) => void;
}

// Single source of truth for marker color — driven by knockout count rather
// than letter grade because for the current Integra cycle the grade is bunched
// at A+ (all curated as priority_1) and would render as a uniform green dot
// field. Knockouts give real visual variance: clean / one-gate / multi-gate.
function markerColor(count: number): { fill: string; border: string } {
  if (count === 0) return { fill: toneStyle('pass').fg, border: toneStyle('pass').border };
  if (count === 1) return { fill: toneStyle('caution').fg, border: toneStyle('caution').border };
  return { fill: toneStyle('fail').fg, border: toneStyle('fail').border };
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

// Bounding box [minLng, minLat, maxLng, maxLat] of a GeoJSON geometry — used to
// fitBounds to a selected pad (pads carry no server-side bbox). Walks the coord
// tree so it handles Polygon and MultiPolygon alike.
function geomBounds(geom: GeoJSON.Geometry): [number, number, number, number] | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (coords: unknown): void => {
    if (Array.isArray(coords) && typeof coords[0] === 'number') {
      const [x, y] = coords as number[];
      if (x < minX) minX = x; if (y < minY) minY = y;
      if (x > maxX) maxX = x; if (y > maxY) maxY = y;
    } else if (Array.isArray(coords)) {
      for (const c of coords) walk(c);
    }
  };
  const c = (geom as { coordinates?: unknown }).coordinates;
  if (c) walk(c);
  if (!isFinite(minX)) return null;
  return [minX, minY, maxX, maxY];
}

// FEMA 100-yr floodplain wash — matches designTokens.floodZone100yr and the
// main map's flood overlay so the color reads consistently across the product.
const FLOOD_COLOR = '#dc2626';
const TWDB_FLOOD_COLOR = '#2563eb';
const CLEANPAD_COLOR = '#15803d';  // clean buildable pad — flood + wetland netted out

// Zoom at/above which the TWDB overlay follows the map viewport instead of the
// whole cycle bbox, and the highest zoom the endpoint's precomputed display
// bands answer — above 14 it switches to the full-res path and rejects any bbox
// wider than a fraction of a degree, so never ask for more than 14.
const TWDB_VIEWPORT_MIN_ZOOM = 11;
const TWDB_MAX_DISPLAY_ZOOM = 14;

// The flood overlay is scoped to the Revenant East-TX AOI counties — flood in
// surrounding counties (Shelby, Houston, …) and in the Pecos/WV outlier
// site counties is suppressed so the overlay reads to the area of interest.
const FLOOD_AOI_STATE = 'TX';
const FLOOD_AOI_COUNTIES = ['Angelina', 'Jasper', 'Nacogdoches', 'San Augustine', 'Tyler', 'Trinity', 'Sabine'];
// Bounding box of the AOI counties' union. The flood overlay is a fixed region,
// so it's fetched ONCE for this bbox (not the moving viewport) — no per-pan
// refetch. Includes the three published county products.
const FLOOD_AOI_BBOX = '-95.85,30.20,-93.50,31.90';

// County boundary lines — a cool slate, dashed, so they read as administrative
// orientation rather than data (distinct from the solid pipeline lines).
const COUNTY_COLOR = '#64748b';

// All-Crown-Pine owner-parcel layer (research curation). Flat wash in a neutral
// timber tan so it reads as context beneath the curated tract shapes.
const OWNER_PARCEL_COLOR = '#a16207';
// Slope choropleth ramp for the owner-parcel layer — flat (green) → steep (red),
// driven by avg_slope_pct. East-TX CP land is mostly <10%, so the ramp is tuned
// tight at the low end to still show variance.
const SLOPE_FILL: mapboxgl.Expression = [
  'interpolate', ['linear'], ['coalesce', ['get', 'avg_slope_pct'], 0],
  0, '#15803d', 5, '#65a30d', 10, '#ca8a04', 15, '#ea580c', 25, '#b91c1c',
];

// Crown Pine research overlay layers (fetched lazily, viewport-based). Config-
// driven so the four share one install loop, one load effect, and one pill row.
// 'fill' = polygon disqualifier (habitat); 'line'/'circle' = infrastructure.
const CRITHAB_COLOR = '#c2410c';      // USFWS critical habitat — hazard orange (distinct from flood red)
const TRANSMISSION_COLOR = '#2563eb'; // HV transmission — power blue
const SUBSTATION_COLOR = '#1e3a8a';   // substations — deeper power blue
const HIGHWAY_COLOR = '#78716c';      // highways — muted stone (orientation)
const WELL_COLOR = '#1c1917';         // active wells — classic black well-spot, white stroke carries it on satellite
const DISTRIBUTION_COLOR = '#a8a29e'; // muted stone — deliberately quiet
type OverlayKind = 'fill' | 'line' | 'circle';
interface ResearchOverlay { key: string; label: string; color: string; kind: OverlayKind; url: (bbox: string) => string; }
const RESEARCH_OVERLAYS: ResearchOverlay[] = [
  { key: 'crithab',      label: 'Habitat',      color: CRITHAB_COLOR,      kind: 'fill',   url: b => `/api/critical-habitat/geojson?bbox=${b}` },
  { key: 'transmission', label: 'Transmission', color: TRANSMISSION_COLOR, kind: 'line',   url: b => `/api/power/geojson?type=transmission_line&min_voltage=100&bbox=${b}&include=overlay` },
  { key: 'substations',  label: 'Substations',  color: SUBSTATION_COLOR,   kind: 'circle', url: b => `/api/power/geojson?type=substation&min_voltage=69&bbox=${b}&include=overlay&class=transmission` },
  { key: 'dist',         label: 'Dist. yards',  color: DISTRIBUTION_COLOR, kind: 'circle', url: b => `/api/power/geojson?type=substation&bbox=${b}&include=overlay&class=distribution` },
  { key: 'highways',     label: 'Highways',     color: HIGHWAY_COLOR,      kind: 'line',   url: b => `/api/highways/geojson?bbox=${b}` },
  // Active (non-plugged) wells INSIDE Crown Pine parcels only — ~450 points,
  // served whole (the endpoint ignores bbox) so the layer stays tiny.
  { key: 'wells',        label: 'Wells',        color: WELL_COLOR,         kind: 'circle', url: b => `/api/crownpine-parcels/wells?bbox=${b}` },
];
const overlayLayerIds = (o: ResearchOverlay) =>
  o.kind === 'fill' ? [`ovl-${o.key}-fill`, `ovl-${o.key}-line`]
  : o.kind === 'line' ? [`ovl-${o.key}`, `ovl-${o.key}-planned`, `ovl-${o.key}-points`]
  : [`ovl-${o.key}`];

export const PortfolioMap: React.FC<Props> = ({
  rows,
  cycleBbox,
  onSelect,
  onHover,
  height = 380,
  stuckHeight = 360,
  sticky = true,
  activeParcelId = null,
  shareToken,
  ownerParcels = false,
  pads,
  activePadId = null,
  focusPadId = null,
  hoveredPadId = null,
  onSelectPad,
  onHoverPad,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  // Separate popup for the All-CP owner-parcel hover (kept distinct from the
  // tract-dot popup so hovering a parcel under a dot doesn't fight it).
  const ownerPopupRef = useRef<mapboxgl.Popup | null>(null);
  // Per-pad hover popup (Crown Pine pad mode). Distinct ref so it can't fight
  // the tract-dot or owner-parcel popups.
  const padPopupRef = useRef<mapboxgl.Popup | null>(null);
  // Hover popup shared by the line layers (transmission overlay + gas pipelines).
  const linePopupRef = useRef<mapboxgl.Popup | null>(null);
  // Latest pad-mode inputs, read inside the once-installed map handlers so they
  // stay current without re-binding. padModeRef gates the pad handlers OFF for
  // Integra (which never passes `pads`) → its clean-pad layer is inert as before.
  const padModeRef = useRef(false);
  const padsRef = useRef<PortfolioPad[] | undefined>(pads);
  const onSelectPadRef = useRef(onSelectPad);
  const onHoverPadRef = useRef(onHoverPad);
  useEffect(() => { padModeRef.current = !!(pads && pads.length); padsRef.current = pads; }, [pads]);
  useEffect(() => { onSelectPadRef.current = onSelectPad; }, [onSelectPad]);
  useEffect(() => { onHoverPadRef.current = onHoverPad; }, [onHoverPad]);
  const [ready, setReady] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [hoverParcel, setHoverParcel] = useState<string | null>(null);
  const [styleMode, setStyleMode] = useState<MapStyleMode>('light');
  // "Assemblies" (false, default) shows only the dissolved outlines; "Parcels"
  // (true) additionally reveals the member-parcel subdivisions within each block.
  const [showParcels, setShowParcels] = useState(false);
  // FEMA 100-yr floodplain overlay — off by default so the map opens clean;
  // the viewer flips it on to see the flood context behind each tract.
  const [showFlood, setShowFlood] = useState(false);
  const [pendingFloodLabels, setPendingFloodLabels] = useState<string[]>([]);
  const [floodTruncated, setFloodTruncated] = useState({ effective: false, pending: false });
  const floodRequestRef = useRef(0);
  // TWDB State Flood Plan existing 1% annual-chance hazard. This planning
  // layer stays independent from FEMA's regulatory floodplain overlay.
  const [showTwdbFlood, setShowTwdbFlood] = useState(false);
  // Which band the TWDB overlay is currently holding — 'aoi' = the whole-cycle
  // band-8 silhouette, 'viewport' = a detailed band cut to the current bounds.
  // Null until a response is applied. Read by the moveend handler to skip the
  // refetch when a low-zoom pan would only re-ask for the same silhouette.
  const twdbFloodModeRef = useRef<'aoi' | 'viewport' | null>(null);
  // Monotonic request id, same guard as the viewport loaders below.
  const twdbFloodSeqRef = useRef(0);
  // Clean buildable pad overlay — ON by default; it's the point of this view.
  const [showCleanPad, setShowCleanPad] = useState(true);
  // County boundary lines — orientation reference, off by default.
  const [showCounties, setShowCounties] = useState(false);
  // Crown Pine research layers (only surfaced when ownerParcels prop is set).
  // "All CP" paints every Crown Pine-owned parcel for visual curation; "Slope"
  // switches that layer's fill from a flat wash to a flat→steep choropleth.
  const [showOwnerParcels, setShowOwnerParcels] = useState(false);
  const [showSlope, setShowSlope] = useState(false);
  // Which research overlays (critical habitat / transmission / substations /
  // highways) are on. A set keyed by overlay.key; toggled by the pill row.
  const [overlays, setOverlays] = useState<Set<string>>(new Set());
  const toggleOverlay = useCallback((key: string) => {
    setOverlays(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);
  // Bumped every time installLayers finishes (initial mount + each style
  // swap). All data-dependent effects include this in their deps so they
  // re-fire and repopulate sources / re-apply paint after a swap. This is
  // more reliable than toggling `ready` because React batching can collapse
  // a false→true flip and skip the intermediate state.
  const [styleEpoch, setStyleEpoch] = useState(0);

  // Measure tool (Crown Pine research mode only). measureActiveRef lets the
  // once-mounted click handlers above suppress selection/popups while measuring.
  const [measureActive, setMeasureActive] = useState(false);
  const measureActiveRef = useRef(false);
  measureActiveRef.current = measureActive;
  const measure = useMapMeasurement({ mapRef, ready, styleEpoch, active: measureActive, enabled: ownerParcels });

  // Refs hold the latest callbacks so the once-mounted map handlers always
  // call the current parent state setters, not the initial closure.
  const onSelectRef = useRef(onSelect);
  const onHoverRef = useRef(onHover);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  useEffect(() => { onHoverRef.current = onHover; }, [onHover]);

  // Detect when the user has scrolled past the map's "natural" position.
  // We observe a 1px sentinel placed just above the map shell; when it leaves
  // the top of the viewport, the map is stuck. Sticky positioning handles the
  // visual lock — this just toggles the compact-mode height + drop shadow.
  useEffect(() => {
    if (!sticky || !sentinelRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0, rootMargin: '0px 0px 0px 0px' }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [sticky]);

  // When the container height swaps (full ↔ stuck), Mapbox needs to recompute
  // canvas dimensions. resize() is cheap and idempotent.
  useEffect(() => {
    if (!ready || !mapRef.current) return;
    // RAF avoids racing the CSS height transition end.
    requestAnimationFrame(() => mapRef.current?.resize());
  }, [stuck, ready]);

  // Install all portfolio-specific sources, layers, and event handlers on the
  // map. Called once after initial style load AND again every time the user
  // toggles base style (setStyle wipes all user-added sources/layers).
  // Pipeline line color is slightly brighter on satellite so it still reads
  // against the darker imagery.
  const installLayers = useCallback((m: mapboxgl.Map, mode: MapStyleMode) => {
    const isSat = mode === 'satellite';

    // FEMA 100-yr floodplain overlay — added before everything else so the
    // wash sits at the very bottom (site shapes, pipelines, dots all draw over
    // it). Data + visibility are driven by the showFlood toggle effect below;
    // the source starts empty and the layers hidden.
    m.addSource('portfolio-flood', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-flood-fill',
      source: 'portfolio-flood',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': ['case', ['==', ['get', 'flood_type'], '500yr'], '#f97316', FLOOD_COLOR], 'fill-opacity': isSat ? 0.34 : 0.22 },
    });
    m.addLayer({
      id: 'portfolio-flood-line',
      source: 'portfolio-flood',
      type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': ['case', ['==', ['get', 'flood_type'], '500yr'], '#f97316', FLOOD_COLOR], 'line-width': 0.5, 'line-opacity': 0.5 },
    });

    m.addSource('portfolio-pending-flood', { type: 'geojson', data: EMPTY_FC });
    for (const layer of pendingFloodLayers('portfolio-pending-flood', 'portfolio-pending-flood-fill', 'portfolio-pending-flood-line')) {
      m.addLayer({ ...layer, layout: { visibility: 'none' } });
    }

    // TWDB existing 1% annual-chance hazard — a separate blue planning wash,
    // never merged with or represented as the FEMA regulatory floodplain.
    m.addSource('portfolio-twdb-flood', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-twdb-flood-fill',
      source: 'portfolio-twdb-flood',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': TWDB_FLOOD_COLOR, 'fill-opacity': isSat ? 0.34 : 0.22 },
    });
    m.addLayer({
      id: 'portfolio-twdb-flood-line',
      source: 'portfolio-twdb-flood',
      type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': TWDB_FLOOD_COLOR, 'line-width': 0.7, 'line-opacity': 0.65 },
    });

    // County boundary lines — dashed slate, above the flood wash but beneath the
    // site shapes so tracts stay prominent. Data + visibility driven by the
    // showCounties toggle effect below.
    m.addSource('portfolio-counties', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-counties-line',
      source: 'portfolio-counties',
      type: 'line',
      layout: { visibility: 'none' },
      paint: {
        'line-color': isSat ? '#CBD5E1' : COUNTY_COLOR,
        'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.5, 10, 1, 14, 1.4],
        'line-opacity': isSat ? 0.7 : 0.55,
        'line-dasharray': [3, 2],
      },
    });

    // All-Crown-Pine owner-parcel layer (research curation, Crown Pine mode only).
    // Empty + hidden by default; populated + shown by the showOwnerParcels effect.
    // Sits beneath the curated tract shapes so it reads as context. The fill color
    // flips between a flat tan and the slope choropleth via the showSlope toggle.
    m.addSource('portfolio-owner-parcels', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-owner-parcels-fill',
      source: 'portfolio-owner-parcels',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': OWNER_PARCEL_COLOR, 'fill-opacity': isSat ? 0.3 : 0.2 },
    });
    m.addLayer({
      id: 'portfolio-owner-parcels-line',
      source: 'portfolio-owner-parcels',
      type: 'line',
      layout: { visibility: 'none' },
      paint: { 'line-color': OWNER_PARCEL_COLOR, 'line-width': 0.5, 'line-opacity': isSat ? 0.8 : 0.55 },
    });

    // Research overlays (critical habitat / transmission / substations / highways).
    // Empty + hidden by default; populated + shown by the loadOverlays effect.
    for (const o of RESEARCH_OVERLAYS) {
      m.addSource(`ovl-${o.key}`, { type: 'geojson', data: EMPTY_FC });
      if (o.kind === 'fill') {
        m.addLayer({
          id: `ovl-${o.key}-fill`, source: `ovl-${o.key}`, type: 'fill',
          layout: { visibility: 'none' },
          paint: { 'fill-color': o.color, 'fill-opacity': isSat ? 0.4 : 0.28 },
        });
        m.addLayer({
          id: `ovl-${o.key}-line`, source: `ovl-${o.key}`, type: 'line',
          layout: { visibility: 'none' },
          paint: { 'line-color': o.color, 'line-width': 0.8, 'line-opacity': 0.7 },
        });
      } else if (o.kind === 'line') {
        m.addLayer({
          id: `ovl-${o.key}`, source: `ovl-${o.key}`, type: 'line',
          layout: { visibility: 'none', 'line-cap': 'round' },
          filter: ['==', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing'],
          paint: {
            'line-color': o.color,
            'line-width': ['interpolate', ['linear'], ['zoom'], 6, 0.6, 11, 1.6, 14, 2.6],
            'line-opacity': isSat ? 0.9 : 0.7,
          },
        });
        // Dashed twin for planned/proposed/candidate segments — dasharray isn't
        // data-drivable in Mapbox GL, so non-existing status gets its own layer.
        m.addLayer({
          id: `ovl-${o.key}-planned`, source: `ovl-${o.key}`, type: 'line',
          layout: { visibility: 'none', 'line-cap': 'round' },
          filter: ['!=', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing'],
          paint: { 'line-color': o.color, 'line-width': 1.4, 'line-dasharray': [2, 2], 'line-opacity': 0.8 },
        });
        // Point-geometry twin — endpoint-only circuits (geometry_confidence
        // 'endpoint_only', incl. one existing 345kV line) carry a Point, not
        // a LineString/MultiLineString, so the two line layers above never
        // render them. Small hollow-ring marker reusing the same
        // status-aware stroke semantics as the substation/dist circles
        // (hollow fill for non-existing, muted ring for unverified candidates).
        m.addLayer({
          id: `ovl-${o.key}-points`, source: `ovl-${o.key}`, type: 'circle',
          layout: { visibility: 'none' },
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': 3.5,
            'circle-color': [
              'case',
              ['!=', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing'],
              'rgba(0,0,0,0)',
              o.color,
            ],
            'circle-stroke-width': 1.4,
            'circle-stroke-color': [
              'case',
              ['==', ['coalesce', ['get', 'operational_status'], 'existing'], 'candidate'],
              DISTRIBUTION_COLOR,
              o.color,
            ],
            'circle-opacity': 0.9,
          },
        });
      } else {
        // Status-aware paint (hollow for planned/candidate, muted ring for
        // unverified) only applies to the power overlays (substations/dist) —
        // wells and any future circle overlay keep the original flat paint
        // (verbatim from pre-7a88b9a: white stroke halo for satellite contrast,
        // radius curve 6:2.5/11:4.5/14:7, stroke-width 1).
        const statusAware = o.key === 'substations' || o.key === 'dist';
        m.addLayer({
          id: `ovl-${o.key}`, source: `ovl-${o.key}`, type: 'circle',
          layout: { visibility: 'none' },
          paint: statusAware ? {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 7, 3, 12, 6],
            'circle-color': [
              'case',
              ['all', ['==', ['coalesce', ['get', 'overlay'], false], true],
                      ['!=', ['coalesce', ['get', 'operational_status'], 'existing'], 'existing']],
              'rgba(0,0,0,0)',                                   // planned/approved/candidate = hollow
              o.color,
            ],
            'circle-stroke-width': 1.4,
            'circle-stroke-color': [
              'case',
              ['==', ['coalesce', ['get', 'operational_status'], 'existing'], 'candidate'],
              DISTRIBUTION_COLOR,                                 // unverified = muted ring
              o.color,
            ],
            'circle-opacity': 0.9,
          } : {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 2.5, 11, 4.5, 14, 7],
            'circle-color': o.color,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 1,
            'circle-opacity': 0.9,
          },
        });
      }
    }

    // Site footprint shapes — added FIRST so they render beneath the pipelines
    // and dots. Two source/layer pairs: the dissolved assembly outline (always
    // visible, the "Assemblies" default) and the member parcels (hidden until
    // the Parcels toggle). Fills sit at the bottom; both lines draw above the
    // fills so the block boundary + parcel edges stay crisp; dots stay on top
    // as the sole hover/click target. Fill color = knockout tone (see markerColor).
    m.addSource('portfolio-outlines', { type: 'geojson', data: EMPTY_FC });
    m.addSource('portfolio-parcels', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-outline-fill',
      source: 'portfolio-outlines',
      type: 'fill',
      paint: { 'fill-color': ['get', 'fill'], 'fill-opacity': isSat ? 0.22 : 0.12 },
    });
    m.addLayer({
      id: 'portfolio-parcels-fill',
      source: 'portfolio-parcels',
      type: 'fill',
      layout: { visibility: 'none' },
      paint: { 'fill-color': ['get', 'fill'], 'fill-opacity': isSat ? 0.14 : 0.07 },
    });
    m.addLayer({
      id: 'portfolio-outline-line',
      source: 'portfolio-outlines',
      type: 'line',
      paint: {
        'line-color': ['get', 'fill'],
        'line-width': 1.4,
        'line-opacity': isSat ? 0.95 : 0.75,
      },
    });
    m.addLayer({
      id: 'portfolio-parcels-line',
      source: 'portfolio-parcels',
      type: 'line',
      layout: { visibility: 'none' },
      paint: {
        'line-color': ['get', 'fill'],
        'line-width': 0.7,
        'line-opacity': isSat ? 0.8 : 0.55,
      },
    });

    // Clean buildable pad — the largest contiguous block outside FEMA 100-yr
    // flood + NWI wetlands. Green fill above the outline/parcel fills so the
    // buildable core reads clearly; ON by default (see showCleanPad). Added
    // before pipelines/tracts/dots so those stay on top as hover targets.
    m.addSource('portfolio-clean-pad', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-clean-pad-fill',
      source: 'portfolio-clean-pad',
      type: 'fill',
      paint: { 'fill-color': CLEANPAD_COLOR, 'fill-opacity': isSat ? 0.4 : 0.28 },
    });
    m.addLayer({
      id: 'portfolio-clean-pad-line',
      source: 'portfolio-clean-pad',
      type: 'line',
      paint: { 'line-color': CLEANPAD_COLOR, 'line-width': 1, 'line-opacity': isSat ? 0.95 : 0.7 },
    });
    // Highlight ring for the externally-selected / hovered pad (Crown Pine pad
    // mode). Same source, filtered to the active/hovered pad_id; inert in
    // Integra mode (features carry no pad_id → filter matches nothing).
    m.addLayer({
      id: 'portfolio-clean-pad-active',
      source: 'portfolio-clean-pad',
      type: 'line',
      paint: { 'line-color': REPORT.accent, 'line-width': 2.6, 'line-opacity': 0.95 },
      filter: ['==', ['get', 'pad_id'], ''],
    });

    m.addSource('portfolio-pipelines', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-pipelines',
      source: 'portfolio-pipelines',
      type: 'line',
      paint: {
        'line-color': isSat ? '#FBE7C6' : REPORT.tertiary,
        'line-width': ['interpolate', ['linear'], ['zoom'], 5, 0.4, 10, 1.2, 14, 2.2],
        'line-opacity': isSat ? 0.9 : 0.55,
      },
    });

    m.addSource('portfolio-tracts', { type: 'geojson', data: EMPTY_FC });
    m.addLayer({
      id: 'portfolio-tracts-halo',
      source: 'portfolio-tracts',
      type: 'circle',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 8, 10, 14, 14, 20],
        'circle-color': ['get', 'fill'],
        'circle-opacity': isSat ? 0.32 : 0.18,
        'circle-stroke-width': 0,
      },
    });
    // Active-selection accent ring — wider than the halo, accent-colored,
    // filtered to the externally-driven activeParcelId. Renders BELOW the dot.
    m.addLayer({
      id: 'portfolio-tracts-active-ring',
      source: 'portfolio-tracts',
      type: 'circle',
      filter: ['==', ['get', 'parcel_id'], ''],
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 16, 10, 22, 14, 30],
        'circle-color': 'transparent',
        'circle-stroke-color': REPORT.accent,
        'circle-stroke-width': 2.5,
        'circle-stroke-opacity': 0.9,
      },
    });
    m.addLayer({
      id: 'portfolio-tracts-dot',
      source: 'portfolio-tracts',
      type: 'circle',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 3.5, 10, 6, 14, 9],
        'circle-color': ['get', 'fill'],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.2,
      },
    });

    m.on('mouseenter', 'portfolio-tracts-dot', (e) => {
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      setHoverParcel(p.parcel_id);
      onHoverRef.current?.(p.parcel_id);
      const coords = (f.geometry as GeoJSON.Point).coordinates as [number, number];
      if (popupRef.current) popupRef.current.remove();
      popupRef.current = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 12,
        className: 'portfolio-map-popup',
      })
        .setLngLat(coords)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.4; color: ${REPORT.body}; min-width: 200px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.08em; color: ${REPORT.tertiary}; text-transform: uppercase;">
              Rank ${p.rank || '—'} · ${p.state}
            </div>
            <div style="font-family: ${PORTFOLIO_TOKENS.serif}; font-size: 14px; color: ${REPORT.heading}; margin-top: 2px;">
              ${p.county} County${p.site_label ? ` <span style="color:${REPORT.secondary};font-size:12px;">${p.site_label}</span>` : ''}
            </div>
            <div style="margin-top: 4px; display: flex; gap: 6px; align-items: center; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 9.5px; color: ${REPORT.secondary};">
              <span style="color: ${REPORT.heading}; font-weight: 600;">${p.mw ? `${p.mw} MW ref.` : '— MW'}</span>
              <span>·</span>
              <span>${p.acres || '—'} ac</span>
              <span>·</span>
              <span style="color: ${REPORT.heading}; font-weight: 600;">Grade ${p.grade || '—'}</span>
            </div>
            ${p.gates ? `<div style="margin-top: 4px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.04em; color: ${toneStyle('fail').fg};">Trips: ${p.gates}</div>` : ''}
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'portfolio-tracts-dot', () => {
      m.getCanvas().style.cursor = '';
      if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }
      setHoverParcel(null);
      onHoverRef.current?.(null);
    });
    m.on('click', 'portfolio-tracts-dot', (e) => {
      if (measureActiveRef.current) return;
      const f = e.features?.[0];
      if (!f) return;
      const pid = (f.properties as { parcel_id?: string })?.parcel_id;
      if (pid) onSelectRef.current?.(pid);
    });

    // All-CP owner-parcel hover — individual parcel acreage + slope (Crown Pine
    // research mode). Follows the mouse; only fires when the layer is visible.
    m.on('mousemove', 'portfolio-owner-parcels-fill', (e) => {
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      const ac = p.acreage != null && p.acreage !== '' ? `${Number(p.acreage).toLocaleString()} ac` : '—';
      const slope = p.avg_slope_pct != null && p.avg_slope_pct !== ''
        ? `${Number(p.avg_slope_pct).toFixed(1)}%` : 'n/a';
      if (ownerPopupRef.current) ownerPopupRef.current.remove();
      ownerPopupRef.current = new mapboxgl.Popup({
        closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup',
      })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">
              ${p.owner_name || 'Crown Pine'} · ${p.county || ''}
            </div>
            <div style="margin-top: 3px; display: flex; gap: 8px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">
              <span style="color: ${REPORT.heading}; font-weight: 600;">${ac}</span>
              <span>·</span>
              <span>Slope ${slope}</span>
            </div>
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'portfolio-owner-parcels-fill', () => {
      m.getCanvas().style.cursor = '';
      if (ownerPopupRef.current) { ownerPopupRef.current.remove(); ownerPopupRef.current = null; }
    });

    // Crown Pine pad hover / click — the pad's OWN acreage + slope, and select.
    // padModeRef gates these OFF for Integra (its clean-pad features carry no
    // pad_id), so the Integra map behaves exactly as before.
    m.on('mousemove', 'portfolio-clean-pad-fill', (e) => {
      if (!padModeRef.current) return;
      m.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0];
      if (!f) return;
      const p = f.properties as Record<string, string>;
      onHoverPadRef.current?.(p.pad_id || null);
      const ac = p.acres != null && p.acres !== '' ? `${Number(p.acres).toLocaleString()} ac` : '—';
      const slope = p.slope != null && p.slope !== '' ? `${Number(p.slope).toFixed(1)}%` : 'n/a';
      if (padPopupRef.current) padPopupRef.current.remove();
      padPopupRef.current = new mapboxgl.Popup({
        closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup',
      })
        .setLngLat(e.lngLat)
        .setHTML(`
          <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 130px;">
            <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Buildable pad</div>
            <div style="margin-top: 3px; display: flex; gap: 8px; font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">
              <span style="color: ${REPORT.heading}; font-weight: 600;">${ac}</span>
              <span>·</span>
              <span>Slope ${slope}</span>
            </div>
          </div>
        `)
        .addTo(m);
    });
    m.on('mouseleave', 'portfolio-clean-pad-fill', () => {
      if (!padModeRef.current) return;
      m.getCanvas().style.cursor = '';
      if (padPopupRef.current) { padPopupRef.current.remove(); padPopupRef.current = null; }
      onHoverPadRef.current?.(null);
    });
    m.on('click', 'portfolio-clean-pad-fill', (e) => {
      if (measureActiveRef.current) return;
      if (!padModeRef.current) return;
      const f = e.features?.[0];
      if (!f) return;
      const padId = (f.properties as { pad_id?: string })?.pad_id;
      if (padId) onSelectPadRef.current?.(padId);
    });

    // Provenance popup for power overlay features (click, not hover — hover
    // is owned by the parcel/pad popups). Feature properties are attacker-
    // controllable (ingested from external sources), so every interpolated
    // string is escaped before landing in setHTML.
    const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const onPowerClick = (e: mapboxgl.MapLayerMouseEvent) => {
      if (measureActiveRef.current) return;
      const f = e.features?.[0];
      if (!f) return;
      const p: any = f.properties || {};
      const conf = p.overlay ? `${esc(p.attribute_confidence)} attrs / ${esc(p.geometry_confidence)} location` : 'HIFLD baseline';
      const src = typeof p.source_url === 'string' && /^https?:\/\//i.test(p.source_url)
        ? `<a href="${esc(p.source_url)}" target="_blank" rel="noreferrer" style="color:#2563eb">source</a>`
        : '';
      new mapboxgl.Popup({ closeButton: true, maxWidth: '260px' })
        .setLngLat(e.lngLat)
        .setHTML(`<div style="font:11px/1.45 -apple-system,sans-serif">
          <strong>${esc(p.name) || '(unnamed)'}</strong><br/>
          ${esc(p.operator) || 'operator unknown'} · ${p.voltage_kv ? esc(p.voltage_kv) + ' kV' : '— kV'}<br/>
          ${esc(p.operational_status) || 'existing'} · ${conf} ${src}</div>`)
        .addTo(m);
    };
    // Hover-spec popup shared by the line layers — transmission (voltage /
    // operator) and the always-on gas transmission pipelines (operator /
    // diameter / MW). Follows the cursor; suppressed while measuring.
    const showLineSpecs = (lngLat: mapboxgl.LngLat, html: string) => {
      m.getCanvas().style.cursor = 'pointer';
      if (linePopupRef.current) linePopupRef.current.remove();
      linePopupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 8, className: 'portfolio-map-popup' })
        .setLngLat(lngLat).setHTML(html).addTo(m);
    };
    const hideLineSpecs = () => {
      m.getCanvas().style.cursor = '';
      if (linePopupRef.current) { linePopupRef.current.remove(); linePopupRef.current = null; }
    };
    const txSpecsHTML = (p: any) => `
      <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 150px;">
        <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Transmission line</div>
        <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.name) || '(unnamed)'}</div>
        <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${esc(p.operator) || 'operator unknown'} · ${p.voltage_kv ? esc(p.voltage_kv) + ' kV' : '— kV'}</div>
      </div>`;
    const pipeSpecsHTML = (p: any) => {
      const capacity = pipelineCapacityDisplay(p);
      const mw = capacity.powerMw != null ? `${Math.round(capacity.powerMw).toLocaleString()} MW power screening equivalent` : 'Segment capacity unavailable';
      const dia = p.diameter_inches != null && p.diameter_inches !== '' ? `${Number(p.diameter_inches)}″` : '— dia';
      return `
        <div style="font-family: ${PORTFOLIO_TOKENS.sans}; font-size: 11px; line-height: 1.45; color: ${REPORT.body}; min-width: 160px;">
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 8.5px; letter-spacing: 0.06em; color: ${REPORT.tertiary}; text-transform: uppercase;">Gas transmission pipeline</div>
          <div style="margin-top: 3px; color: ${REPORT.heading}; font-weight: 600;">${esc(p.name) || esc(p.operator) || 'Unknown'}</div>
          <div style="font-family: ${PORTFOLIO_TOKENS.mono}; font-size: 10px; color: ${REPORT.secondary};">${dia} · ${mw}</div>
        </div>`;
    };

    // ownerParcels gates this OFF for Integra (which never passes it) — the
    // power overlays only exist in Crown Pine research mode, so the click
    // handler must be inert there too. Mirrors the pad-mode gating above.
    if (ownerParcels) {
      // Click → provenance popup (substations, dist yards, transmission lines).
      for (const lid of ['ovl-substations', 'ovl-dist', 'ovl-transmission', 'ovl-transmission-planned']) {
        m.on('click', lid, onPowerClick);
      }
      // Hover → spec popup for transmission lines.
      for (const lid of ['ovl-transmission', 'ovl-transmission-planned']) {
        m.on('mousemove', lid, (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, txSpecsHTML(f.properties || {})); });
        m.on('mouseleave', lid, hideLineSpecs);
      }
      // Hover → spec popup for the always-on gas transmission pipelines (answers
      // "what are those lines?" — operator, diameter, MW potential).
      m.on('mousemove', 'portfolio-pipelines', (e) => { if (measureActiveRef.current) return; const f = e.features?.[0]; if (f) showLineSpecs(e.lngLat, pipeSpecsHTML(f.properties || {})); });
      m.on('mouseleave', 'portfolio-pipelines', hideLineSpecs);
    }
  }, [ownerParcels]);

  // 1. Mount the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const token = getMapboxToken();
    if (!token) {
      console.warn('[PortfolioMap] No VITE_MAPBOX_TOKEN set — map disabled');
      return;
    }
    mapboxgl.accessToken = token;

    const initialBounds = cycleBbox || [-104, 25, -78, 41]; // continental US-ish fallback
    const m = new mapboxgl.Map({
      container: containerRef.current,
      style: STYLE_URLS.light,
      bounds: [
        [initialBounds[0], initialBounds[1]],
        [initialBounds[2], initialBounds[3]],
      ],
      fitBoundsOptions: { padding: 40, duration: 0 },
      attributionControl: false,
    });
    m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    m.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');

    m.on('load', () => {
      installLayers(m, 'light');
      setReady(true);
      setStyleEpoch(e => e + 1);
    });

    mapRef.current = m;
    return () => {
      if (popupRef.current) popupRef.current.remove();
      if (ownerPopupRef.current) ownerPopupRef.current.remove();
      m.remove();
      mapRef.current = null;
    };
    // Mount once. Bounds re-fit handled by the rows/bbox effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Style swap — when the user toggles satellite/light, setStyle wipes all
  // user-added sources/layers. We re-install them once the new style is
  // loaded then bump styleEpoch so every data-dependent effect re-fires and
  // repopulates its source / re-applies its paint.
  const prevStyleModeRef = useRef<MapStyleMode>('light');
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    if (prevStyleModeRef.current === styleMode) return;
    prevStyleModeRef.current = styleMode;
    const onStyleLoad = () => {
      installLayers(m, styleMode);
      setStyleEpoch(e => e + 1);
    };
    m.once('style.load', onStyleLoad);
    m.setStyle(STYLE_URLS[styleMode]);
  }, [styleMode, ready, installLayers]);

  // 2a. Push centroids into the tracts layer when rows change OR after a
  //     style swap (styleEpoch bump). Source population only — camera fit is
  //     a separate effect so style swaps don't reset the user's camera.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const features: GeoJSON.Feature[] = rows
      .filter(r => r.centroid)
      .map(r => {
        const c = markerColor(r.knockouts.count);
        const potential = pipelinePotential(r);
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [r.centroid!.lng, r.centroid!.lat] },
          properties: {
            parcel_id: r.parcel_id,
            rank: r.rank ?? '',
            state: r.state,
            county: r.county,
            site_label: r.site_label || '',
            grade: r.grade || '',
            mw: potential.kind === 'modeled' ? `≈${potential.displayMw.toLocaleString('en-US')}` : '',
            acres: r.key_numbers.acres ?? '',
            gates: r.knockouts.list.length ? r.knockouts.list.map(k => k.toUpperCase()).join(' · ') : '',
            fill: c.fill,
            border: c.border,
            count: r.knockouts.count,
          },
        };
      });
    const src = m.getSource('portfolio-tracts') as mapboxgl.GeoJSONSource | undefined;
    if (src) src.setData({ type: 'FeatureCollection', features });
  }, [ready, rows, styleEpoch]);

  // 2a-shapes. Push footprint geometry into the outline + parcel sources.
  //   Each feature carries the row's knockout color so fills/lines match the
  //   dot semantics. Runs on rows change and after a style swap (styleEpoch).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const outlineFeatures: GeoJSON.Feature[] = [];
    const parcelFeatures: GeoJSON.Feature[] = [];
    const cleanPadFeatures: GeoJSON.Feature[] = [];
    for (const r of rows) {
      const c = markerColor(r.knockouts.count);
      const props = { parcel_id: r.parcel_id, fill: c.fill, border: c.border, count: r.knockouts.count };
      if (r.geometry) {
        outlineFeatures.push({ type: 'Feature', geometry: r.geometry, properties: props });
      }
      if (r.member_geometries) {
        for (const g of r.member_geometries) {
          parcelFeatures.push({ type: 'Feature', geometry: g, properties: props });
        }
      }
      // Integra: one clean-pad MultiPolygon per block. Crown Pine pad mode
      // instead draws one feature per pad (built below), so skip the block form.
      if (!pads && r.clean_pad_geometry) {
        cleanPadFeatures.push({ type: 'Feature', geometry: r.clean_pad_geometry, properties: { parcel_id: r.parcel_id } });
      }
    }
    // Crown Pine pad mode — one independent, individually-keyed feature per pad
    // so it can be clicked, hovered, highlighted, and flown-to on its own.
    if (pads) {
      for (const pad of pads) {
        if (!pad.geometry) continue;
        cleanPadFeatures.push({
          type: 'Feature',
          geometry: pad.geometry,
          properties: {
            pad_id: pad.id,
            parcel_id: pad.parent?.parcel_id ?? '',
            acres: pad.acres ?? null,
            slope: pad.avg_slope_pct ?? null,
          },
        });
      }
    }
    const outlineSrc = m.getSource('portfolio-outlines') as mapboxgl.GeoJSONSource | undefined;
    if (outlineSrc) outlineSrc.setData({ type: 'FeatureCollection', features: outlineFeatures });
    const parcelSrc = m.getSource('portfolio-parcels') as mapboxgl.GeoJSONSource | undefined;
    if (parcelSrc) parcelSrc.setData({ type: 'FeatureCollection', features: parcelFeatures });
    const cleanPadSrc = m.getSource('portfolio-clean-pad') as mapboxgl.GeoJSONSource | undefined;
    if (cleanPadSrc) cleanPadSrc.setData({ type: 'FeatureCollection', features: cleanPadFeatures });
  }, [ready, rows, pads, styleEpoch]);

  // 2a-toggle. Show/hide the member-parcel layers when the user flips the
  //   Assemblies/Parcels control. styleEpoch reapplies after a style swap
  //   (which resets every layer to its installed default visibility).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const vis = showParcels ? 'visible' : 'none';
    for (const id of ['portfolio-parcels-fill', 'portfolio-parcels-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
  }, [showParcels, ready, styleEpoch]);

  // 2c-toggle. Show/hide the clean-pad layers. Mirrors the parcels toggle;
  // styleEpoch reapplies after a style swap (which resets installed defaults).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const vis = showCleanPad ? 'visible' : 'none';
    for (const id of ['portfolio-clean-pad-fill', 'portfolio-clean-pad-line', 'portfolio-clean-pad-active']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
  }, [showCleanPad, ready, styleEpoch]);

  // 2b. Fit to cycle bbox — fires only when cycleBbox itself changes, NOT on
  //     style swaps (a swap would otherwise jerk the user back from their
  //     current zoom/pan position).
  useEffect(() => {
    if (!ready || !mapRef.current || !cycleBbox) return;
    mapRef.current.fitBounds(
      [[cycleBbox[0], cycleBbox[1]], [cycleBbox[2], cycleBbox[3]]],
      { padding: 50, duration: 300 }
    );
  }, [ready, cycleBbox]);

  // 3. Fetch pipeline corridors for the cycle bbox. Re-fetches on style swap
  //    because setStyle wipes the source data along with the layers.
  useEffect(() => {
    if (!ready || !mapRef.current || !cycleBbox || styleEpoch === 0) return;
    const m = mapRef.current;
    const bbox = cycleBbox.join(',');
    // min_diameter=12 thresholds out small laterals by recorded pipe size, not
    // by a modeled MW claim; it draws the same lines the retired min_mw=400
    // floor did. zoom=8 picks a moderate simplification level (~500m tolerance).
    // Share-link viewers don't have a localStorage Bearer; route auth via
    // ?token= instead. portfolio_share_tokens-authed requests bypass simpleAuth
    // and are allowed on /api/pipelines/geojson via markPortfolioScopeAllowed.
    const base = `/api/pipelines/geojson?capacity_semantics=2&bbox=${bbox}&zoom=8&group=gas_transmission&min_diameter=12`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => r.ok ? r.json() : EMPTY_FC)
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-pipelines') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[PortfolioMap] pipelines fetch failed:', err));
  }, [ready, cycleBbox, styleEpoch, shareToken]);

  // Effective and pending FEMA are distinct sources under the same Flood control.
  // Fixed county bounds avoid refetching on pan; both requests retain share auth.
  const loadFlood = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showFlood ? 'visible' : 'none';
    const request = ++floodRequestRef.current;
    for (const id of ['portfolio-flood-fill', 'portfolio-flood-line', 'portfolio-pending-flood-fill', 'portfolio-pending-flood-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showFlood) return;
    // Fetch the whole AOI once (fixed bbox), not the moving viewport — the county
    // clip already scopes it, so there's nothing to re-fetch as the user pans.
    const aoi = `&state=${FLOOD_AOI_STATE}&counties=${encodeURIComponent(FLOOD_AOI_COUNTIES.join(','))}`;
    const base = `/api/flood-zones/geojson?bbox=${FLOOD_AOI_BBOX}&zoom=10${aoi}&fema_release=2026-09-26`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection & { truncated?: boolean }) => {
        if (request !== floodRequestRef.current || mapRef.current !== m) return;
        const src = m.getSource('portfolio-flood') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
        setFloodTruncated(previous => ({ ...previous, effective: fc.truncated === true }));
      })
      .catch(err => console.warn('[PortfolioMap] flood fetch failed:', err));
    const pendingUrl = url.replace('/flood-zones/geojson?', '/flood-zones/pending/geojson?');
    fetch(pendingUrl, { headers })
      .then(r => { if (!r.ok) throw new Error('Pending FEMA request failed'); return r.json(); })
      .then((fc: GeoJSON.FeatureCollection & { truncated?: boolean }) => {
        if (request !== floodRequestRef.current || mapRef.current !== m) return;
        const src = m.getSource('portfolio-pending-flood') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
        setPendingFloodLabels(pendingFemaLabels(fc));
        setFloodTruncated(previous => ({ ...previous, pending: fc.truncated === true }));
      })
      .catch(err => {
        if (request !== floodRequestRef.current || mapRef.current !== m) return;
        (m.getSource('portfolio-pending-flood') as mapboxgl.GeoJSONSource | undefined)?.setData(EMPTY_FC);
        setPendingFloodLabels([]);
        console.warn('[PortfolioMap] pending FEMA fetch failed:', err);
      });
  }, [ready, showFlood, shareToken]);

  // Flood is a fixed-AOI overlay: load once on toggle/style-swap, no moveend
  // refetch (that was the pan lag).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    loadFlood();
  }, [showFlood, ready, styleEpoch, loadFlood]);

  // 3c. TWDB State Flood Plan existing 1% hazard — zoom-adaptive, two modes.
  //     Zoomed out it loads the whole cycle bbox at band 8: a 1.1km-tolerance
  //     county silhouette, fine as a screening cartoon but wildly wrong up
  //     close, where it breaks into spiky slivers nowhere near the real hazard
  //     edge. From z11 it follows the viewport and asks for the band matching
  //     the zoom instead — band 12 sits within a few percent of full-res truth.
  //     The zoom is capped at 14 because above that the endpoint leaves the
  //     precomputed bands for the full-res path and 400s on anything but a tiny
  //     bbox. Panning at low zoom does NOT refetch: the silhouette already
  //     covers the whole cycle, and that refetch is the pan lag the FEMA
  //     overlay above shed.
  const loadTwdbFlood = useCallback((force = false) => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showTwdbFlood ? 'visible' : 'none';
    for (const id of ['portfolio-twdb-flood-fill', 'portfolio-twdb-flood-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (!showTwdbFlood) return;
    const mode: 'aoi' | 'viewport' = m.getZoom() < TWDB_VIEWPORT_MIN_ZOOM ? 'aoi' : 'viewport';
    // A low-zoom pan re-asks for the silhouette already on the map — skip it.
    if (mode === 'aoi' && !force && twdbFloodModeRef.current === 'aoi') return;
    let bbox: string;
    let zoom: number;
    if (mode === 'aoi') {
      if (!cycleBbox) return;
      bbox = cycleBbox.join(',');
      zoom = 8;
    } else {
      const b = m.getBounds();
      bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
      zoom = Math.min(Math.round(m.getZoom()), TWDB_MAX_DISPLAY_ZOOM);
    }
    const base = `/api/twdb-flood-hazards/geojson?bbox=${bbox}&zoom=${zoom}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    const seq = ++twdbFloodSeqRef.current;
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        if (seq !== twdbFloodSeqRef.current) return;   // a newer viewport already won
        const src = m.getSource('portfolio-twdb-flood') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData({ type: 'FeatureCollection', features: fc.features || [] });
        twdbFloodModeRef.current = mode;
      })
      .catch(err => console.warn('[PortfolioMap] TWDB flood fetch failed:', err));
  }, [ready, showTwdbFlood, cycleBbox, shareToken]);

  // Toggle and style swap force a full reload — a swap recreates the source, so
  // whatever band the mode ref remembers is no longer on the map. While the
  // layer is on, a debounced moveend keeps the viewport mode following the
  // bounds; the aoi mode skips inside the loader.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    twdbFloodModeRef.current = null;
    loadTwdbFlood(true);
    if (!showTwdbFlood) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onMove = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => { t = null; loadTwdbFlood(); }, 400);
    };
    m.on('moveend', onMove);
    return () => { if (t) clearTimeout(t); m.off('moveend', onMove); };
  }, [showTwdbFlood, ready, styleEpoch, loadTwdbFlood]);

  // 3d. County boundary lines — same pattern as the flood overlay: toggle
  //     visibility, and when on fetch county outlines for the current viewport.
  const loadCounties = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    if (m.getLayer('portfolio-counties-line')) {
      m.setLayoutProperty('portfolio-counties-line', 'visibility', showCounties ? 'visible' : 'none');
    }
    if (!showCounties) return;
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const z = Math.round(m.getZoom());
    const base = `/api/county-boundaries/geojson?bbox=${bbox}&zoom=${z}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-counties') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[PortfolioMap] counties fetch failed:', err));
  }, [ready, showCounties, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadCounties();
    if (!showCounties) return;
    const onMove = () => loadCounties();
    m.on('moveend', onMove);
    return () => { m.off('moveend', onMove); };
  }, [showCounties, ready, styleEpoch, loadCounties]);

  // 3d. All-Crown-Pine owner parcels (research curation) — same viewport-fetch
  //     pattern as counties. The showSlope toggle repaints the same layer's fill
  //     between a flat tan and the slope choropleth (no refetch needed).
  const loadOwnerParcels = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const vis = showOwnerParcels ? 'visible' : 'none';
    for (const id of ['portfolio-owner-parcels-fill', 'portfolio-owner-parcels-line']) {
      if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', vis);
    }
    if (m.getLayer('portfolio-owner-parcels-fill')) {
      m.setPaintProperty('portfolio-owner-parcels-fill', 'fill-color',
        showSlope ? SLOPE_FILL : OWNER_PARCEL_COLOR);
    }
    if (!showOwnerParcels) return;
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const z = Math.round(m.getZoom());
    const base = `/api/crownpine-parcels/geojson?bbox=${bbox}&zoom=${z}`;
    const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
    const headers: Record<string, string> = {};
    if (!shareToken) {
      const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
      if (password) headers['Authorization'] = `Bearer ${password}`;
    }
    fetch(url, { headers })
      .then(r => (r.ok ? r.json() : EMPTY_FC))
      .then((fc: GeoJSON.FeatureCollection) => {
        const src = m.getSource('portfolio-owner-parcels') as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(fc);
      })
      .catch(err => console.warn('[PortfolioMap] owner-parcels fetch failed:', err));
  }, [ready, showOwnerParcels, showSlope, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadOwnerParcels();
    if (!showOwnerParcels) return;
    const onMove = () => loadOwnerParcels();
    m.on('moveend', onMove);
    return () => { m.off('moveend', onMove); };
  }, [showOwnerParcels, showSlope, ready, styleEpoch, loadOwnerParcels]);

  // 3e. Research overlays — one generic viewport loader for all four (habitat /
  //     transmission / substations / highways). Sets each layer's visibility and
  //     fetches the active ones for the current viewport (moveend refetch).
  const loadOverlays = useCallback(() => {
    const m = mapRef.current;
    if (!m || !ready) return;
    const b = m.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].join(',');
    const password = typeof window !== 'undefined' ? localStorage.getItem('pp-auth') : null;
    for (const o of RESEARCH_OVERLAYS) {
      const on = overlays.has(o.key);
      for (const id of overlayLayerIds(o)) {
        if (m.getLayer(id)) m.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
      }
      if (!on) continue;
      const base = o.url(bbox);
      const url = shareToken ? `${base}&token=${encodeURIComponent(shareToken)}` : base;
      const headers: Record<string, string> = {};
      if (!shareToken && password) headers['Authorization'] = `Bearer ${password}`;
      fetch(url, { headers })
        .then(r => (r.ok ? r.json() : EMPTY_FC))
        .then((fc: GeoJSON.FeatureCollection) => {
          const src = m.getSource(`ovl-${o.key}`) as mapboxgl.GeoJSONSource | undefined;
          if (src) src.setData(fc);
        })
        .catch(err => console.warn(`[PortfolioMap] ${o.key} fetch failed:`, err));
    }
  }, [ready, overlays, shareToken]);

  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    loadOverlays();
    if (overlays.size === 0) return;
    const onMove = () => loadOverlays();
    m.on('moveend', onMove);
    return () => { m.off('moveend', onMove); };
  }, [overlays, ready, styleEpoch, loadOverlays]);

  // 4. Highlight the hovered marker. styleEpoch reapplies the paint property
  //    on the freshly-installed dot layer after a style swap.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    if (m.getLayer('portfolio-tracts-dot')) {
      m.setPaintProperty('portfolio-tracts-dot', 'circle-stroke-width', [
        'case',
        ['==', ['get', 'parcel_id'], hoverParcel || ''],
        2.4,
        1.2,
      ]);
    }
  }, [hoverParcel, ready, styleEpoch]);

  // 5. Externally-driven active selection — filters the accent-ring layer and
  //    zooms the map to the picked tract. Always zooms in (vs. the previous
  //    "stay put if in view" behavior) so the user sees the parcel in context
  //    even from a cold cycle-wide bbox. fitBounds with maxZoom keeps tiny
  //    parcels from getting cranked to street level.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    if (m.getLayer('portfolio-tracts-active-ring')) {
      m.setFilter('portfolio-tracts-active-ring', ['==', ['get', 'parcel_id'], activeParcelId || '']);
    }
    // Thicken the active site's outline to match the accent ring.
    if (m.getLayer('portfolio-outline-line')) {
      m.setPaintProperty('portfolio-outline-line', 'line-width', [
        'case',
        ['==', ['get', 'parcel_id'], activeParcelId || ''],
        2.8,
        1.4,
      ]);
    }
    // Crown Pine pad mode drives its own flyTo off the selected PAD (effect 5b),
    // so don't also fly to the parent block's bbox here — they'd fight.
    if (padModeRef.current) return;
    if (!activeParcelId) return;
    const row = rows.find(r => r.parcel_id === activeParcelId);
    if (!row) return;
    if (row.bbox) {
      // Prefer bbox-based fitBounds — adapts zoom to actual parcel extent.
      // maxZoom 11 ≈ neighborhood detail with surrounding context still
      // visible; 1100ms ease feels deliberate rather than jarring.
      m.fitBounds(
        [[row.bbox[0], row.bbox[1]], [row.bbox[2], row.bbox[3]]],
        { padding: 100, maxZoom: 11, duration: 1100 }
      );
    } else if (row.centroid) {
      m.flyTo({ center: [row.centroid.lng, row.centroid.lat], zoom: 10, speed: 0.9, curve: 1.4 });
    }
  }, [activeParcelId, ready, rows, styleEpoch]);

  // 5b. Crown Pine pad HIGHLIGHT (no zoom) — ring the selected (or, failing
  //     that, hovered) pad. Selection deliberately does NOT move the map, so
  //     the user can scan the list without it lurching. Zoom is effect 5c.
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0) return;
    const m = mapRef.current;
    const highlightId = activePadId || hoveredPadId || '';
    if (m.getLayer('portfolio-clean-pad-active')) {
      m.setFilter('portfolio-clean-pad-active', ['==', ['get', 'pad_id'], highlightId]);
    }
  }, [activePadId, hoveredPadId, ready, styleEpoch]);

  // 5b-move. Selecting a card MOVES the map to that pad (recenters; lifts to a
  //   regional zoom if still cycle-wide). Deliberately NOT keyed on hover, so
  //   scanning rows doesn't jerk the map. A tighter zoom-in is the side-panel
  //   action (effect 5c).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0 || !activePadId || !pads) return;
    const pad = pads.find(p => p.id === activePadId);
    if (!pad?.geometry) return;
    const b = geomBounds(pad.geometry);
    if (!b) return;
    mapRef.current.easeTo({
      center: [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2],
      zoom: Math.max(mapRef.current.getZoom(), 9),
      duration: 700,
    });
  }, [activePadId, ready, pads, styleEpoch]);

  // 5c. Crown Pine pad ZOOM — fires ONLY when a pad's side panel is opened
  //     (focusPadId), never on plain selection. Pads carry no server bbox, so
  //     bounds come from the geometry (geomBounds).
  useEffect(() => {
    if (!ready || !mapRef.current || styleEpoch === 0 || !focusPadId || !pads) return;
    const pad = pads.find(p => p.id === focusPadId);
    if (!pad?.geometry) return;
    const b = geomBounds(pad.geometry);
    if (b) {
      mapRef.current.fitBounds([[b[0], b[1]], [b[2], b[3]]], { padding: 90, maxZoom: 12.5, duration: 1000 });
    }
  }, [focusPadId, ready, pads, styleEpoch]);

  const effectiveHeight = stuck ? stuckHeight : height;

  return (
    <>
      {/* Sentinel for IntersectionObserver — sits at the map's natural
          position; when it leaves the viewport upward, the map locks. */}
      {sticky && <div ref={sentinelRef} style={{ height: 1, marginTop: 24 }} />}
      <section style={{
        position: sticky ? 'sticky' : 'static',
        top: 0,
        zIndex: 20,
        marginBottom: 0,
        border: `1px solid ${REPORT.rule}`,
        borderRadius: 0,
        overflow: 'hidden',
        background: REPORT.white,
        boxShadow: stuck ? '0 6px 14px -8px rgba(46,56,64,0.22)' : 'none',
        transition: 'box-shadow 180ms ease',
      }}>
        <header style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          padding: stuck ? '6px 14px' : '10px 14px',
          borderBottom: `1px solid ${REPORT.rule}`,
          background: REPORT.surface,
          transition: 'padding 180ms ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, rowGap: 6, flexWrap: 'wrap' }}>
            <div style={{
              fontFamily: PORTFOLIO_TOKENS.mono,
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: REPORT.heading,
            }}>
              Portfolio Map{stuck ? ' · sticky' : ''}
            </div>
            <StyleToggle mode={styleMode} onChange={setStyleMode} />
            <ShapeToggle showParcels={showParcels} onChange={setShowParcels} />
            <CleanPadToggle active={showCleanPad} onChange={setShowCleanPad} />
            <FloodToggle active={showFlood} onChange={setShowFlood} />
            <PillToggle label="TWDB Existing 1% Hazard" color={TWDB_FLOOD_COLOR}
              active={showTwdbFlood} onChange={setShowTwdbFlood} />
            <CountyToggle active={showCounties} onChange={setShowCounties} />
            {ownerParcels && (
              <>
                <PillToggle label="All CP" color={OWNER_PARCEL_COLOR} active={showOwnerParcels} onChange={setShowOwnerParcels} />
                <PillToggle label="Slope" color="#ca8a04" active={showSlope} onChange={(v) => {
                  // Slope is a repaint of the Crown Pine parcel layer, so turning
                  // it on must also switch that layer on — otherwise the toggle
                  // looks broken (nothing to repaint). One click = slope visible.
                  setShowSlope(v);
                  if (v) setShowOwnerParcels(true);
                }} />
                {RESEARCH_OVERLAYS.map(o => (
                  <PillToggle key={o.key} label={o.label} color={o.color}
                    active={overlays.has(o.key)} onChange={() => toggleOverlay(o.key)} />
                ))}
                <PillToggle label="Measure" color="#ef4444" active={measureActive} onChange={setMeasureActive} />
                {measureActive && measure.count > 0 && (
                  <button
                    type="button"
                    onClick={measure.clearAll}
                    style={{
                      fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, fontWeight: 700,
                      letterSpacing: '0.10em', textTransform: 'uppercase', padding: '4px 9px',
                      cursor: 'pointer', background: REPORT.white, color: REPORT.secondary,
                      border: `1px solid ${PORTFOLIO_TOKENS.ruleMid}`, borderRadius: 3,
                    }}
                  >
                    Clear ({measure.count})
                  </button>
                )}
              </>
            )}
          </div>
        </header>
        {/* Legend — its OWN row below the toggles so appearing/disappearing
            items never shift the button positions above. */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 14,
          rowGap: 3,
          padding: '5px 14px',
          borderBottom: `1px solid ${REPORT.rule}`,
          background: REPORT.white,
          fontFamily: PORTFOLIO_TOKENS.mono,
          fontSize: 9,
          letterSpacing: '0.04em',
          color: REPORT.secondary,
        }}>
          <LegendDot color={toneStyle('pass').fg} label="Clean" />
          <LegendDot color={toneStyle('caution').fg} label="1 gate" />
          <LegendDot color={toneStyle('fail').fg} label="≥2 gates" />
          {showCleanPad && <LegendDot color={CLEANPAD_COLOR} label="Clean pad" />}
          {showFlood && <LegendDot color={FLOOD_COLOR} label="Effective FEMA · 1% annual chance" />}
          {showFlood && <LegendDot color="#f97316" label="Effective FEMA · 0.2% annual chance" />}
          {showFlood && pendingFloodLabels.map(label => <LegendDot key={label} color={PENDING_FEMA_COLOR} label={label} />)}
          {showFlood && (floodTruncated.effective || floodTruncated.pending) && <span style={{ color: REPORT.tertiary }}>FEMA overlay is incomplete at this extent.</span>}
          {showTwdbFlood && <LegendDot color={TWDB_FLOOD_COLOR} label="TWDB existing 1% annual-chance hazard" />}
          {showTwdbFlood && <span style={{ color: REPORT.tertiary }}>Targeted coverage: Frio + 20 Crown Pine ETX counties. TWDB planning compilation; may include modeled and regulatory source data; not itself a FEMA flood-zone determination.</span>}
          {showCounties && <LegendDot color={COUNTY_COLOR} label="County line" />}
          {showOwnerParcels && !showSlope && <LegendDot color={OWNER_PARCEL_COLOR} label="All Crown Pine" />}
          {showOwnerParcels && showSlope && <span>Slope: <span style={{ color: '#15803d' }}>flat</span> → <span style={{ color: '#b91c1c' }}>steep</span></span>}
          {RESEARCH_OVERLAYS.filter(o => overlays.has(o.key)).map(o => (
            <LegendDot key={o.key} color={o.color} label={o.label} />
          ))}
          {measureActive && (
            <span style={{ color: '#ef4444', fontWeight: 600 }}>Measuring — click to add points · dbl-click / Esc to finish</span>
          )}
          {!stuck && (
            <>
              <span style={{ color: REPORT.tertiary }}>·</span>
              <span>Gas transmission lines ≥12″</span>
            </>
          )}
        </div>
        <div
          ref={containerRef}
          style={{
            width: '100%',
            height: effectiveHeight,
            transition: 'height 180ms ease',
          }}
        />
      </section>
    </>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
    <span style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: 4,
      background: color,
      boxShadow: '0 0 0 1px #ffffff',
    }} />
    {label}
  </span>
);

// Segmented control — base style switch. Active state uses REPORT.heading for
// strong selection contrast; inactive is ghost border so the control reads as
// optional chrome rather than a primary action.
const StyleToggle: React.FC<{
  mode: MapStyleMode;
  onChange: (m: MapStyleMode) => void;
}> = ({ mode, onChange }) => {
  const btn = (target: MapStyleMode, position: 'left' | 'right'): React.CSSProperties => ({
    fontFamily: PORTFOLIO_TOKENS.mono,
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    padding: '4px 9px',
    cursor: 'pointer',
    background: mode === target ? REPORT.heading : REPORT.white,
    color: mode === target ? REPORT.white : REPORT.secondary,
    border: `1px solid ${mode === target ? REPORT.heading : PORTFOLIO_TOKENS.ruleMid}`,
    borderRadius: position === 'left' ? '3px 0 0 3px' : '0 3px 3px 0',
    marginRight: position === 'left' ? -1 : 0,
    transition: 'all 0.12s ease',
  });
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button type="button" style={btn('light', 'left')} onClick={() => onChange('light')}>Map</button>
      <button type="button" style={btn('satellite', 'right')} onClick={() => onChange('satellite')}>Aerial</button>
    </div>
  );
};

// Segmented control — footprint detail. "Assemblies" shows the dissolved site
// outlines; "Parcels" additionally reveals the member-parcel subdivisions.
// Same visual grammar as StyleToggle so the two controls read as a set.
const ShapeToggle: React.FC<{
  showParcels: boolean;
  onChange: (v: boolean) => void;
}> = ({ showParcels, onChange }) => {
  const btn = (active: boolean, position: 'left' | 'right'): React.CSSProperties => ({
    fontFamily: PORTFOLIO_TOKENS.mono,
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
    padding: '4px 9px',
    cursor: 'pointer',
    background: active ? REPORT.heading : REPORT.white,
    color: active ? REPORT.white : REPORT.secondary,
    border: `1px solid ${active ? REPORT.heading : PORTFOLIO_TOKENS.ruleMid}`,
    borderRadius: position === 'left' ? '3px 0 0 3px' : '0 3px 3px 0',
    marginRight: position === 'left' ? -1 : 0,
    transition: 'all 0.12s ease',
  });
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
      <button type="button" style={btn(!showParcels, 'left')} onClick={() => onChange(false)}>Assemblies</button>
      <button type="button" style={btn(showParcels, 'right')} onClick={() => onChange(true)}>Parcels</button>
    </div>
  );
};

// Single on/off pill — FEMA floodplain overlay. Active state uses the flood red
// (matching the wash + legend dot) so the control's color states what it shows.
const FloodToggle: React.FC<{
  active: boolean;
  onChange: (v: boolean) => void;
}> = ({ active, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!active)}
    aria-pressed={active}
    style={{
      fontFamily: PORTFOLIO_TOKENS.mono,
      fontSize: 8.5,
      fontWeight: 700,
      letterSpacing: '0.10em',
      textTransform: 'uppercase',
      padding: '4px 9px',
      cursor: 'pointer',
      background: active ? FLOOD_COLOR : REPORT.white,
      color: active ? REPORT.white : REPORT.secondary,
      border: `1px solid ${active ? FLOOD_COLOR : PORTFOLIO_TOKENS.ruleMid}`,
      borderRadius: 3,
      transition: 'all 0.12s ease',
    }}
  >
    Flood
  </button>
);

// Single on/off pill — county boundary lines. Active state uses the county slate
// so the control's color states what it shows (matches the line + legend dot).
const CountyToggle: React.FC<{
  active: boolean;
  onChange: (v: boolean) => void;
}> = ({ active, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!active)}
    aria-pressed={active}
    style={{
      fontFamily: PORTFOLIO_TOKENS.mono,
      fontSize: 8.5,
      fontWeight: 700,
      letterSpacing: '0.10em',
      textTransform: 'uppercase',
      padding: '4px 9px',
      cursor: 'pointer',
      background: active ? COUNTY_COLOR : REPORT.white,
      color: active ? REPORT.white : REPORT.secondary,
      border: `1px solid ${active ? COUNTY_COLOR : PORTFOLIO_TOKENS.ruleMid}`,
      borderRadius: 3,
      transition: 'all 0.12s ease',
    }}
  >
    Counties
  </button>
);

// Single on/off pill — clean buildable pad overlay. Active state uses the pad
// green so the control's color states what it shows (matches fill + legend dot).
const CleanPadToggle: React.FC<{
  active: boolean;
  onChange: (v: boolean) => void;
}> = ({ active, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!active)}
    aria-pressed={active}
    style={{
      fontFamily: PORTFOLIO_TOKENS.mono,
      fontSize: 8.5,
      fontWeight: 700,
      letterSpacing: '0.10em',
      textTransform: 'uppercase',
      padding: '4px 9px',
      cursor: 'pointer',
      background: active ? CLEANPAD_COLOR : REPORT.white,
      color: active ? REPORT.white : REPORT.secondary,
      border: `1px solid ${active ? CLEANPAD_COLOR : PORTFOLIO_TOKENS.ruleMid}`,
      borderRadius: 3,
      transition: 'all 0.12s ease',
    }}
  >
    Clean pad
  </button>
);

// Generic on/off pill — Crown Pine research toggles (All CP, Slope). Active state
// fills with the passed color so the control states what it shows. `disabled`
// greys it out (Slope is meaningless until All CP is on).
const PillToggle: React.FC<{
  label: string;
  color: string;
  active: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ label, color, active, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!active)}
    aria-pressed={active}
    disabled={disabled}
    style={{
      fontFamily: PORTFOLIO_TOKENS.mono,
      fontSize: 8.5,
      fontWeight: 700,
      letterSpacing: '0.10em',
      textTransform: 'uppercase',
      padding: '4px 9px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      background: active ? color : REPORT.white,
      color: active ? REPORT.white : REPORT.secondary,
      border: `1px solid ${active ? color : PORTFOLIO_TOKENS.ruleMid}`,
      borderRadius: 3,
      transition: 'all 0.12s ease',
    }}
  >
    {label}
  </button>
);

export default PortfolioMap;
